import React from "react";
import { Outlet, Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  ClipboardCheck,
  MapPin,
  School,
  Target,
} from "lucide-react";
import { SQAAF_SIDEBAR_ITEMS } from "@/components/sqaaf/sqaafConstants";
import SidebarUserCard from "@/components/SidebarUserCard";
import { ScopeProvider } from "@/context/ScopeContext";
import "../pages/SqafDashboard.css";

const SIDEBAR_ICONS = {
  executive: BarChart3,
  blocks: MapPin,
  sections: Target,
  schools: School,
  intervention: AlertTriangle,
};

const StandaloneSqafLayout = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "executive";

  const handleLogout = () => {
    if (onLogout) onLogout();
    navigate("/login");
  };

  const goToTab = (tabId) => {
    const next = new URLSearchParams(searchParams);
    if (tabId === "executive") next.delete("tab");
    else next.set("tab", tabId);
    navigate({ pathname: "/sqaaf-dashboard", search: next.toString() ? `?${next.toString()}` : "" });
  };

  return (
    <ScopeProvider>
    <div className="sqaf-dashboard-root">
      <div className="app-shell">
        <aside className="sq-sidebar" aria-label="SQAAF dashboard sidebar">
          <div className="sq-sidebar-brand">
            <div className="sq-sidebar-brand-main">
              <span className="sq-brand-icon" aria-hidden="true">
                <ClipboardCheck className="w-[30px] h-[30px]" strokeWidth={1.75} />
              </span>
              <span className="sq-brand-copy">
                <strong>SQAAF</strong>
                <span>School Quality Assessment</span>
              </span>
            </div>
          </div>

          <div className="sq-sidebar-scroll">
            <section className="sq-sidebar-section">
              <div className="sq-section-title">Sections</div>
              <nav className="sq-nav">
                {SQAAF_SIDEBAR_ITEMS.map(({ id, label }) => {
                  const Icon = SIDEBAR_ICONS[id] || BarChart3;
                  return (
                    <button
                      key={id}
                      type="button"
                      className={`sq-nav-item${activeTab === id ? " active" : ""}`}
                      onClick={() => goToTab(id)}
                      data-testid={`sqaaf-nav-${id}`}
                    >
                      <Icon strokeWidth={2} />
                      {label}
                    </button>
                  );
                })}
              </nav>
            </section>
          </div>

          <div className="sq-sidebar-footer">
            <SidebarUserCard user={user} onLogout={handleLogout} />
          </div>
        </aside>

        <main className="main">
          <header className="topbar">
            <div className="topbar-inner">
              <div className="top-title">
                <Link className="back-link" to="/">
                  <ArrowLeft className="svg-icon" strokeWidth={2} />
                  Back to MH Education
                </Link>
                <div className="title-mini">
                  <span className="mini-icon">
                    <ClipboardCheck className="svg-icon" strokeWidth={2} />
                  </span>
                  <div>
                    <strong>SQAAF Dashboard</strong>
                    <span>School Quality Assessment & Accreditation</span>
                  </div>
                </div>
              </div>
              <div className="top-actions" />
            </div>
          </header>
          <Outlet />
        </main>
      </div>
    </div>
    </ScopeProvider>
  );
};

export default StandaloneSqafLayout;
